// AUTO-GENERATED — do not edit. Source: /shared/. Run scripts/sync-shared.sh to update.
/**
 * BPI (Barber Performance Index) — the rework (Jose 2026-09-27).
 *
 * The old system was 17 hand-dragged 0-100 sliders (incl. MONEY numbers anyone could slide) blended into
 * a 0-30 score, and a SEPARATE 0-100 engine on the profile card — two disagreeing scores. This is the one
 * authoritative model. Design:
 *
 *  • Every parameter has a KIND:
 *      'computed' = value comes from REAL data (growth snapshot / reporting / time-clock). Read-only —
 *                   nobody slides it, not even the owner. The owner only sets weight + on/off.
 *      'manual'   = the owner sets the 0-100 value per barber (teamwork, communication, …).
 *  • Two orthogonal switches per parameter:
 *      track        = do we record/show it at all.
 *      affectsScore = does it move the BPI score (a param can be tracked but NOT scored — e.g. no-show).
 *  • weight = relative contribution when affectsScore. Renormalized over the params that actually score
 *             AND have a value, so turning one off (or a computed value being unavailable) never skews it.
 *  • Transparent scoring: computeBpi returns the per-parameter point contributions so the UI can show the
 *    exact math ("Rebooking 90 × 18% = 16.2 pts … Base 71 − 3 adj = 68").
 *  • Manual adjustment: a bounded ± nudge with a REQUIRED reason (enforced in the UI); notes feed the AI.
 *
 * Config (which params exist, kind, track, affects, weight, owner-added params) is owner-editable in
 * Settings → Growth & Bonus and persisted as settings type 'bpiConfig'. Per-barber MANUAL values +
 * adjustment live in bpiData/{barberId}. Score is 0-100 (unifies with the profile card).
 */

export type BpiParamKind = 'computed' | 'manual';

/** Where a computed param pulls its real 0-100 value. The service resolver maps these to live data. */
export type BpiComputedSource =
  | 'occupancy'       // growth snapshot — fill-the-book (the revenue lever)
  | 'returning'       // growth snapshot — returning-client %
  | 'rebooking'       // growth snapshot — rebooking % (backed by the real checkout rebook signal)
  | 'retail'          // growth snapshot — retail % of service revenue
  | 'rating'          // growth snapshot — client rating (awaiting reviews until the Fresha pull)
  | 'avgTicket'       // reporting — average ticket, normalized vs target
  | 'serviceSpeed'    // appointments — scheduled ÷ actual duration
  | 'noShowRate'      // appointments — no-show % (0-100, higher = worse; invert when scored)
  | 'newClients';     // reporting — new-client acquisition, normalized vs target

export type BpiCategory =
  | 'Core Performance'
  | 'Service Quality'
  | 'Professional Standards'
  | 'Growth & Development'
  | 'Client Relations';

export interface BpiParam {
  key: string;                 // stable id (matches the legacy bpiData factor keys where possible)
  label: string;
  description: string;
  category: BpiCategory;
  kind: BpiParamKind;
  source?: BpiComputedSource;  // required when kind === 'computed'
  track: boolean;              // record/show it
  affectsScore: boolean;       // move the score
  weight: number;              // relative weight when affectsScore (renormalized)
  invert?: boolean;            // true → lower raw value is better (e.g. no-show); scored as 100 - value
  manualDefault?: number;      // seed for a manual param's per-barber value (0-100)
  custom?: boolean;            // owner-added param (safe to delete in the editor)
}

export interface BpiConfig {
  params: BpiParam[];
  /** Bound for the manual ± adjustment, on the 0-100 score scale. */
  adjustmentMax: number;
}

/**
 * Canonical default parameter set. Mirrors the old 5 categories/17 factors so existing manual values
 * migrate, but with Jose's rework baked in: money metrics are COMPUTED (read-only), no-show is track-only,
 * punctuality/skill/uniform/cleanliness/social ship OFF (owner flips them on), teamwork + client
 * communication score by default (he explicitly values them).
 */
export const DEFAULT_BPI_CONFIG: BpiConfig = {
  adjustmentMax: 5,
  params: [
    // ── Core Performance — all COMPUTED from real data (read-only) ──
    { key: 'revenuePerformance', label: 'Revenue Performance', description: 'Fills the book — occupancy, the revenue lever', category: 'Core Performance', kind: 'computed', source: 'occupancy', track: true, affectsScore: true, weight: 18 },
    { key: 'clientRetention',    label: 'Client Retention',    description: 'Clients who come back to this barber',        category: 'Core Performance', kind: 'computed', source: 'returning', track: true, affectsScore: true, weight: 16 },
    { key: 'averageTicket',      label: 'Average Ticket',      description: 'Average ticket vs target',                    category: 'Core Performance', kind: 'computed', source: 'avgTicket', track: true, affectsScore: true, weight: 10 },
    { key: 'productSalesRate',   label: 'Product Sales Rate',  description: 'Retail as a % of service revenue',            category: 'Core Performance', kind: 'computed', source: 'retail',   track: true, affectsScore: true, weight: 8 },

    // ── Service Quality ──
    { key: 'clientSatisfaction', label: 'Client Satisfaction', description: 'Average client rating (from reviews)',        category: 'Service Quality', kind: 'computed', source: 'rating',       track: true, affectsScore: true, weight: 14 },
    { key: 'serviceSpeed',       label: 'Service Speed',       description: 'Scheduled ÷ actual time — also gates emergency booking', category: 'Service Quality', kind: 'computed', source: 'serviceSpeed', track: true, affectsScore: true, weight: 8 },
    { key: 'rebookRate',         label: 'Rebook Rate',         description: 'Books the next cut at checkout',              category: 'Service Quality', kind: 'computed', source: 'rebooking',    track: true, affectsScore: true, weight: 12 },
    { key: 'noShowRate',         label: 'No-Show Rate',        description: 'Tracked only — outside the barber’s control',  category: 'Service Quality', kind: 'computed', source: 'noShowRate',   track: true, affectsScore: false, weight: 0, invert: true },

    // ── Professional Standards — punctuality computed-capable but OFF until turned on; rest manual ──
    { key: 'punctuality',           label: 'Punctuality',            description: 'On-time (from the time clock) — off until enabled', category: 'Professional Standards', kind: 'computed', source: 'serviceSpeed', track: true, affectsScore: false, weight: 0 },
    { key: 'uniformCompliance',     label: 'Uniform Compliance',     description: 'Dress-code adherence',       category: 'Professional Standards', kind: 'manual', track: true, affectsScore: false, weight: 0, manualDefault: 90 },
    { key: 'workstationCleanliness',label: 'Workstation Cleanliness',description: 'Station + shared-space upkeep', category: 'Professional Standards', kind: 'manual', track: true, affectsScore: false, weight: 0, manualDefault: 90 },

    // ── Growth & Development — manual; teamwork scores, the rest ship off ──
    { key: 'teamwork',        label: 'Teamwork',          description: 'Covers the sweep/towels/phones when the attendant is out', category: 'Growth & Development', kind: 'manual', track: true, affectsScore: true,  weight: 6, manualDefault: 75 },
    { key: 'skillDevelopment',label: 'Skill Development', description: 'Training / certification progress', category: 'Growth & Development', kind: 'manual', track: true, affectsScore: false, weight: 0, manualDefault: 70 },
    { key: 'initiative',      label: 'Initiative',        description: 'Proactive, takes ownership',        category: 'Growth & Development', kind: 'manual', track: true, affectsScore: false, weight: 0, manualDefault: 70 },

    // ── Client Relations — communication scores (Jose: it matters); acquisition track-only; social off ──
    { key: 'clientCommunication',  label: 'Client Communication',  description: 'Consultation + rapport at the chair',       category: 'Client Relations', kind: 'manual', track: true, affectsScore: true,  weight: 8, manualDefault: 80 },
    { key: 'newClientAcquisition', label: 'New Client Acquisition',description: 'New clients earned — tracked only',         category: 'Client Relations', kind: 'computed', source: 'newClients', track: true, affectsScore: false, weight: 0 },
    { key: 'socialMediaEngagement',label: 'Social Media Engagement',description: 'Promotes the shop on social',              category: 'Client Relations', kind: 'manual', track: true, affectsScore: false, weight: 0, manualDefault: 60 },
  ],
};

/** Fill/repair a persisted (possibly partial or older) BpiConfig against the defaults. Pure.
 *  Unknown keys from storage are kept (owner-added params); default keys missing from storage are added. */
export function mergeBpiConfig(p?: Partial<BpiConfig> | null): BpiConfig {
  const d = DEFAULT_BPI_CONFIG;
  const adjustmentMax = typeof p?.adjustmentMax === 'number' && isFinite(p.adjustmentMax) ? p.adjustmentMax : d.adjustmentMax;
  const stored = Array.isArray(p?.params) ? p!.params : [];
  const byKey = new Map<string, BpiParam>();
  // Start from defaults so a new default param appears even for shops with an older saved config…
  for (const dp of d.params) byKey.set(dp.key, { ...dp });
  // …then overlay the owner's saved values (weights/toggles/kind/etc.) and keep any custom params.
  for (const sp of stored) {
    if (!sp || typeof sp.key !== 'string') continue;
    const base = byKey.get(sp.key);
    byKey.set(sp.key, base ? { ...base, ...sp } : normalizeCustomParam(sp));
  }
  return { adjustmentMax, params: Array.from(byKey.values()) };
}

function normalizeCustomParam(sp: Partial<BpiParam>): BpiParam {
  return {
    key: sp.key || `custom_${Math.abs(hashKey(JSON.stringify(sp)))}`,
    label: sp.label || 'Custom parameter',
    description: sp.description || '',
    category: (sp.category as BpiCategory) || 'Growth & Development',
    kind: sp.kind === 'computed' ? 'computed' : 'manual',
    source: sp.source,
    track: sp.track !== false,
    affectsScore: sp.affectsScore === true,
    weight: typeof sp.weight === 'number' ? sp.weight : 0,
    invert: sp.invert === true,
    manualDefault: typeof sp.manualDefault === 'number' ? sp.manualDefault : 60,
    custom: true,
  };
}

function hashKey(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) { h = (h << 5) - h + s.charCodeAt(i); h |= 0; }
  return h;
}

export interface BpiContribution {
  key: string;
  label: string;
  value: number;        // the 0-100 value scored (already inverted for invert params)
  rawValue: number;     // the value before inversion (what's displayed)
  weight: number;       // effective (renormalized) weight, 0-100
  points: number;       // value * effectiveWeight / 100 — the actual contribution to the base score
  kind: BpiParamKind;
  available: boolean;   // false for a computed param with no data yet (e.g. rating awaiting reviews)
}

export interface BpiResult {
  base: number;              // 0-100 weighted score before adjustment
  adjustment: number;        // clamped ± nudge
  final: number;             // clamp(base + adjustment, 0, 100)
  maxScore: 100;
  contributions: BpiContribution[];  // scoring params (affectsScore), for the transparent breakdown
  scoredWeight: number;      // sum of effective weights that actually contributed (diagnostic)
}

/**
 * Transparent BPI compute. Pure.
 * @param config         the parameter definitions (owner config)
 * @param computedValues resolved real 0-100 values by param key (or null when a computed param has no data)
 * @param manualValues   per-barber 0-100 values by param key for manual params
 * @param adjustment     the manual ± nudge (clamped to ±config.adjustmentMax)
 */
export function computeBpi(
  config: BpiConfig,
  computedValues: Record<string, number | null | undefined>,
  manualValues: Record<string, number | null | undefined>,
  adjustment = 0,
): BpiResult {
  const scoring = config.params.filter(p => p.affectsScore && p.weight > 0);

  // Resolve each scoring param to a value + availability.
  const rows = scoring.map(p => {
    let raw: number | null;
    if (p.kind === 'computed') {
      const v = computedValues[p.key];
      raw = typeof v === 'number' && isFinite(v) ? v : null;
    } else {
      const v = manualValues[p.key];
      raw = typeof v === 'number' && isFinite(v) ? v : (typeof p.manualDefault === 'number' ? p.manualDefault : null);
    }
    const available = raw !== null;
    const clampedRaw = available ? Math.max(0, Math.min(100, raw as number)) : 0;
    const value = p.invert ? 100 - clampedRaw : clampedRaw;
    return { p, raw: clampedRaw, value, available };
  });

  // Renormalize weights over params that ACTUALLY contribute (available).
  const totalWeight = rows.reduce((s, r) => s + (r.available ? r.p.weight : 0), 0);
  const contributions: BpiContribution[] = rows.map(r => {
    const effWeight = totalWeight > 0 && r.available ? (r.p.weight / totalWeight) * 100 : 0;
    const points = (r.value * effWeight) / 100;
    return {
      key: r.p.key, label: r.p.label, value: r.value, rawValue: r.raw,
      weight: Math.round(effWeight * 10) / 10, points: Math.round(points * 10) / 10,
      kind: r.p.kind, available: r.available,
    };
  });

  const base = Math.round(contributions.reduce((s, c) => s + c.points, 0) * 10) / 10;
  const adjMax = Math.abs(config.adjustmentMax || 0);
  const clampedAdj = Math.max(-adjMax, Math.min(adjMax, adjustment || 0));
  const final = Math.max(0, Math.min(100, Math.round((base + clampedAdj) * 10) / 10));

  return { base, adjustment: clampedAdj, final, maxScore: 100, contributions, scoredWeight: totalWeight };
}

/** Rank band for a 0-100 BPI (display only). */
export function bpiRank(score: number): { label: string; color: string } {
  if (score >= 90) return { label: 'Elite', color: 'text-amber-500' };
  if (score >= 75) return { label: 'Strong', color: 'text-green-500' };
  if (score >= 60) return { label: 'Solid', color: 'text-blue-500' };
  if (score >= 40) return { label: 'Developing', color: 'text-purple-400' };
  return { label: 'Needs focus', color: 'text-gray-400' };
}
